import { useState } from "react";
import { api } from "@/lib/soul";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Heart, PenLine } from "lucide-react";

const FEELINGS = ["Comforted", "Convicted", "Challenged", "At peace", "Unsettled", "Seen", "Hopeful", "Tender"];

// A gentle "how did that sit with you?" check-in shown after a person reads something.
export function FeltCheck({ sourcePrompt, testId = "felt-check" }) {
  const { user } = useAuth();
  const isPaid = user && (user.tier === "seeker" || user.tier === "contributor");
  const [feeling, setFeeling] = useState("");
  const [note, setNote] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const pick = (f) => {
    setFeeling(f);
    setOpen(true);
  };

  const keep = async () => {
    setSaving(true);
    try {
      await api.post("/journal", {
        prompt: sourcePrompt,
        content: `I felt: ${feeling}.${note.trim() ? ` ${note.trim()}` : ""}`,
        mood: feeling,
      });
      toast.success("Kept in your journal.");
      setOpen(false); setFeeling(""); setNote("");
    } catch {
      toast.error("Could not keep it just now.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={testId} className="mt-6 pt-5 border-t border-white/5">
      <p className="flex items-center gap-2 text-sm text-[#B4A897] mb-3">
        <Heart size={14} className="text-[#D4AF37]" /> How did that sit with you?
      </p>
      <div className="flex flex-wrap gap-2">
        {FEELINGS.map((f) => (
          <button
            key={f}
            data-testid={`${testId}-feeling-${f.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => pick(f)}
            className={`text-sm px-3 py-1.5 rounded-full border transition-colors duration-300 ${
              feeling === f ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 text-[#A8A29E] hover:border-[#D4AF37]/40"
            }`}
          >
            {f}
          </button>
        ))}
      </div>
      {open && (
        <div className="mt-4 fade-up">
          <p className="italic-serif text-[#F5F1E7] text-lg">
            Sit with “{feeling.toLowerCase()}” for a moment. What is it showing you?
          </p>
          <textarea
            data-testid={`${testId}-note`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="A quiet word to yourself… (optional)"
            className="mt-3 w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-[#F7F1E6] outline-none focus:border-[#D4AF37]/50 transition-colors duration-300 resize-none placeholder:text-[#8a7f70]"
          />
          {isPaid ? (
            <button
              data-testid={`${testId}-keep`}
              onClick={keep}
              disabled={saving}
              className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] px-5 py-2 text-sm hover:bg-[#D4AF37]/10 transition-colors duration-300 disabled:opacity-50"
            >
              <PenLine size={14} /> {saving ? "Keeping…" : "Keep this in my journal"}
            </button>
          ) : (
            <p className="mt-3 text-xs text-[#8a7f70]">
              Let it rest here, or <a href="/pricing" className="text-[#D4AF37] hover:underline">become a Seeker</a> to keep it in your journal.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
